import React, { useRef, useEffect } from 'react';

interface SimpleZoomPanProps {
  children: React.ReactNode;
  externalTransform?: { scale: number; translateX: number; translateY: number } | null; // External transform to apply (from gesture handlers via store)
}

/**
 * SimpleZoomPan: CSS-only transform applier
 * 
 * This component ONLY applies CSS transforms from the parent.
 * All gesture handling (pinch, wheel, pan) is done by useNewModeGestures or useGestures hooks
 * and synced to appStore, then passed via externalTransform prop.
 * 
 * Purpose: Decouple gesture handling from CSS rendering
 */
export const SimpleZoomPan: React.FC<SimpleZoomPanProps> = ({ children, externalTransform }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Apply external transform (from parent hook via store)
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    
    if (externalTransform) {
      const transformStr = `translate(${externalTransform.translateX}px, ${externalTransform.translateY}px) scale(${externalTransform.scale})`;
      content.style.transform = transformStr;
      // console.log(`🎨 [SimpleZoomPan] Applied transform: ${transformStr}`);
      
      // console.log(`   Container ref bounds: `, containerRef.current?.getBoundingClientRect());
      // console.log(`   Content ref bounds: `, contentRef.current?.getBoundingClientRect());
    } else {
      content.style.transform = 'translate(0px, 0px) scale(1)';
      // console.log(`🎨 [SimpleZoomPan] Reset to identity transform`);
    }
  }, [externalTransform]);

  return (
    <div 
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <div 
        ref={contentRef}
        style={{
          transformOrigin: '0 0',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      >
        {children}
      </div>
    </div>
  );
};
